import { UzAlert } from '@utazzo/ui';
import { ArrowLeft, Check, Lock } from 'lucide-react';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { FdActivitiesStep } from '../components/FdActivitiesStep';
import { FdConfigureStep } from '../components/FdConfigureStep';
import { FdDaysStep } from '../components/FdDaysStep';
import { FdDeparturesStep } from '../components/FdDeparturesStep';
import { FdFlightsStep } from '../components/FdFlightsStep';
import { FdHotelsStep } from '../components/FdHotelsStep';
import { FdInclusionsStep } from '../components/FdInclusionsStep';
import { FdLandPackageStep } from '../components/FdLandPackageStep';
import { FdPricingStep } from '../components/FdPricingStep';
import { useFixedDepartureDetail } from '../use-fixed-departures';

import { useListReturn } from '@/hooks/use-list-return';

/**
 * Fixed Departure builder - the same guided stepper shape as the itinerary
 * builder, but every field is hand-entered and nothing is priced live:
 *   1. Configure - title, supplier, cities(+nights) -> generates the day slots
 *   2. Days      - the generated day-by-day plan, written by hand
 *   3. Pricing   - grade tiers and the tier x group-size price grid
 *   4. Hotels    - option pool, grouped under the tier it belongs to
 *   5. Rate      - markup, validity window, vehicle supplements
 *   6. Flights   - block flight option pool (these DO carry dates)
 *   7. Inclusions
 *   8. Activities- optional paid add-ons
 * Every step after Configure stays locked until the day slots exist, because
 * they are what the rest of the template hangs off.
 */

type StepKey =
  | 'configure'
  | 'departures'
  | 'days'
  | 'pricing'
  | 'hotels'
  | 'land'
  | 'flights'
  | 'inclusions'
  | 'activities';

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'configure', label: 'Configure' },
  { key: 'departures', label: 'Departures' },
  { key: 'days', label: 'Days' },
  { key: 'pricing', label: 'Tiers & Pricing' },
  { key: 'hotels', label: 'Hotels' },
  { key: 'land', label: 'Rate & Supplements' },
  { key: 'flights', label: 'Flights' },
  { key: 'inclusions', label: 'Inclusions' },
  { key: 'activities', label: 'Activities' },
];

const STATUS_META: Record<string, { label: string; dot: string }> = {
  DRAFT: { label: 'Draft', dot: 'bg-zinc-400' },
  PUBLISHED: { label: 'Published', dot: 'bg-emerald-500' },
  ARCHIVED: { label: 'Archived', dot: 'bg-rose-500' },
};

export const FixedDepartureDetailPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const backToList = useListReturn('/fixed-departures');
  const detailQuery = useFixedDepartureDetail(id);
  const fd = detailQuery.data;
  const [step, setStep] = useState<StepKey>('configure');

  const hasDays = (fd?.days.length ?? 0) > 0;
  const stepEnabled = (key: StepKey): boolean => (key === 'configure' ? true : hasDays);
  const currentIndex = STEPS.findIndex((s) => s.key === step);
  const status = fd ? STATUS_META[fd.status] : null;

  const renderStep = (): React.ReactNode => {
    if (!fd) return null;
    switch (step) {
      case 'configure':
        return <FdConfigureStep fd={fd} onSaved={() => setStep('days')} />;
      case 'departures':
        return <FdDeparturesStep fd={fd} />;
      case 'days':
        return <FdDaysStep fd={fd} />;
      case 'pricing':
        return <FdPricingStep fd={fd} />;
      case 'hotels':
        return <FdHotelsStep fd={fd} />;
      case 'land':
        return <FdLandPackageStep fd={fd} />;
      case 'flights':
        return <FdFlightsStep fd={fd} />;
      case 'inclusions':
        return <FdInclusionsStep fd={fd} />;
      case 'activities':
        return <FdActivitiesStep fd={fd} />;
      default:
        return null;
    }
  };

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to fixed departures"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Fixed Departures</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {fd?.title ?? 'Loading...'}
          </span>
        </div>
        {fd && <span className="font-mono text-xs text-zinc-400">{fd.reference}</span>}
        <span className="flex-1" />
        {fd && fd.numberOfNights > 0 && (
          <span className="text-xs text-zinc-500">
            {fd.numberOfNights}N / {fd.numberOfDays}D
          </span>
        )}
        {status && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + status.dot} aria-hidden />
            {status.label}
          </span>
        )}
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {detailQuery.isLoading && (
          <div className="space-y-4">
            <div className="h-5 w-2/3 animate-pulse rounded bg-zinc-100" />
            <div className="h-24 animate-pulse rounded-lg border border-zinc-200 bg-zinc-50/60" />
            <div className="h-64 animate-pulse rounded-lg border border-zinc-200 bg-zinc-50/60" />
          </div>
        )}
        {detailQuery.isError && (
          <UzAlert tone="destructive">
            Failed to load this fixed departure. It may have been deleted.
          </UzAlert>
        )}

        {fd && (
          <>
            {/* Stepper rail - sticky flush against the top bar (h-12), matching
                the itinerary builder's offsets so the two bars never gap. */}
            <div className="sticky top-6 z-20 -mx-6 mb-6 border-b border-zinc-200 bg-white px-6 pt-2 pb-2 lg:top-4 lg:-mx-8 lg:px-8">
              <ol className="flex min-w-0 flex-wrap items-center gap-1.5">
                {STEPS.map((s, i) => {
                  const enabled = stepEnabled(s.key);
                  const active = s.key === step;
                  const done = enabled && i < currentIndex;
                  return (
                    <li key={s.key} className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={!enabled}
                        onClick={() => enabled && setStep(s.key)}
                        className={
                          'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ' +
                          (active
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : enabled
                              ? 'hover:bg-accent hover:text-accent-foreground text-zinc-700'
                              : 'cursor-not-allowed text-zinc-300')
                        }
                      >
                        <span
                          className={
                            'inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ' +
                            (active
                              ? 'text-primary-foreground bg-white/25'
                              : done
                                ? 'bg-primary/10 text-primary'
                                : enabled
                                  ? 'bg-zinc-100 text-zinc-600'
                                  : 'bg-zinc-100 text-zinc-300')
                          }
                        >
                          {!enabled ? (
                            <Lock className="h-3 w-3" aria-hidden />
                          ) : done ? (
                            <Check className="h-3 w-3" aria-hidden />
                          ) : (
                            i + 1
                          )}
                        </span>
                        {s.label}
                      </button>
                      {i < STEPS.length - 1 && (
                        <span className="text-zinc-300" aria-hidden>
                          -
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>

            {!hasDays && step === 'configure' && (
              <UzAlert tone="info" className="mb-6">
                Add the cities and nights below, then save. That generates the day slots and unlocks
                the rest of the builder.
              </UzAlert>
            )}

            {renderStep()}
          </>
        )}
      </div>
    </div>
  );
};
